// ═══════════════════════════════════════════════════════════════════════════
// VARIANTI E LAVORI EXTRA — regole pure (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Revisione 15/10/2026 (decisione di Paolo): la variante può nascere anche come OFFERTA,
// prima del lavoro. Due fatti indipendenti: il cliente l'ha ACCETTATA? il lavoro è ESEGUITO?
//   • ricavo (totale commessa e margine): solo se accettata (o fatturata);
//   • costo materiali previsto della variante: solo se accettata OPPURE eseguita;
//   • un'offerta solo valorizzata non pesa su nulla (compare come "in attesa").
// Stati: daValorizzare → offerta / inAttesa (eseguita, da accettare) / accettata (da
// eseguire) → valorizzata (accettata ed eseguita = da fatturare) → fatturata;
// oppure "non fatturabile" (a carico nostro: ricavo 0, i costi restano in commessa).
// Le voci di prima (senza i campi accettata/eseguita) sono eseguite se fatturate o con la data
// del lavoro; sono accettate se avevano l'autorizzazione del cliente scritta o se sono fatturate.
// Il costo previsto NON entra nei costi reali (verrebbe contato due volte con l'ordine vero):
// va nel costo previsto della commessa e nel margine previsto (ControlloEconomico).
// Il budget originale (importo contrattuale) NON cambia mai: le varianti hanno un pannello
// a parte, e "Budget + Varianti accettate = Totale commessa" è la base di ricavi e margine.
// Collezione: attivitaExtra (nome storico). L'importo resta nel campo valoreEconomico.
// ═══════════════════════════════════════════════════════════════════════════
import { parseValutaItaliana } from "./clienteHelpers";

// "nelDocumento": come la si cita in relazioni e riepiloghi (decisione di Paolo, 15/10/2026:
// le varianti si citano come richieste o indispensabili, non come «già eseguite»)
export const ORIGINI_VARIANTE = {
  richiesta_cliente:  { id: "richiesta_cliente",  label: "Richiesta del cliente", colore: "#2563eb", nelDocumento: "richiesta dal cliente" },
  variazione_tecnica: { id: "variazione_tecnica", label: "Variazione tecnica",    colore: "#d97706", nelDocumento: "variante tecnica indispensabile" },
};

// Chi ha deciso l'importo della variante (si possono indicare entrambi)
export const DECISORI_IMPORTO = { pm: "PM", titolare: "Titolare" };

/** "PM e Titolare" / "PM" / "" */
export function testoValorizzataDa(v) {
  const l = (Array.isArray(v?.valorizzataDa) ? v.valorizzataDa : []).map(k => DECISORI_IMPORTO[k]).filter(Boolean);
  return l.length === 2 ? `${l[0]} e ${l[1]}` : (l[0] || "");
}

export const STATI_VARIANTE = {
  daValorizzare:  { id: "daValorizzare",  label: "Da valorizzare",  colore: "#c2410c", bg: "#ffedd5" },
  offerta:        { id: "offerta",        label: "Offerta · da accettare",  colore: "#0369a1", bg: "#e0f2fe" },
  inAttesa:       { id: "inAttesa",       label: "Eseguita · da accettare", colore: "#b45309", bg: "#fef3c7" },
  accettata:      { id: "accettata",      label: "Accettata · da eseguire", colore: "#1d4ed8", bg: "#dbeafe" },
  valorizzata:    { id: "valorizzata",    label: "Da fatturare",    colore: "#7c3aed", bg: "#ede9fe" },
  fatturata:      { id: "fatturata",      label: "Fatturata",       colore: "#059669", bg: "#d1fae5" },
  nonFatturabile: { id: "nonFatturabile", label: "Non fatturabile", colore: "#6b7280", bg: "#f3f4f6" },
  esclusa:        { id: "esclusa",        label: "Esclusa",         colore: "#9ca3af", bg: "#f9fafb" },
};

/** Importo della variante (null se non ancora stabilito). Legge anche gli importi scritti all'italiana. */
export function importoVariante(v) {
  return leggiImporto(v?.valoreEconomico);
}

/**
 * Importo scritto dall'utente → numero, oppure null se vuoto o non leggibile
 * ("da definire", "abc", "-" non diventano mai 0 €: restano "da valorizzare").
 */
export function leggiImporto(x) {
  if (x === null || x === undefined) return null;
  if (typeof x === "number") return Number.isFinite(x) ? x : null;
  const t = String(x).trim();
  if (t === "" || !/\d/.test(t)) return null;
  const n = parseValutaItaliana(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Lavoro eseguito? Conta il campo «eseguita» quando c'è (tutte le varianti dal 15/10/2026).
 * Le voci di prima, senza il campo, NON si considerano più eseguite a priori (W409: comparivano
 * «eseguite» varianti mai fatte): lo sono se fatturate, con stato «Eseguita» o con la data del
 * lavoro scritta; un'offerta senza data resta da eseguire.
 */
export function eEseguita(v) {
  if (typeof v?.eseguita === "boolean") return v.eseguita || v?.fatturata === true;
  const s = String(v?.stato || "").toLowerCase();
  if (v?.fatturata === true || s === "fatturata" || s === "eseguita") return true;
  if (s === "offerta" || s === "da eseguire") return false;
  return String(v?.data || "").trim() !== "";
}

/** Accettata dal cliente? Le voci di prima: sì se fatturate o con l'autorizzazione scritta. */
export function eAccettata(v) {
  if (typeof v?.accettata === "boolean") return v.accettata || v?.fatturata === true || String(v?.stato || "").toLowerCase() === "fatturata";
  return v?.fatturata === true || String(v?.stato || "").toLowerCase() === "fatturata" || String(v?.autorizzazione || "").trim() !== "";
}

/** Costo materiali previsto della variante (null se non indicato). */
export function costoPrevistoVariante(v) {
  return leggiImporto(v?.costoMaterialiPrevisto);
}

/** Il costo previsto conta solo se la variante è accettata oppure eseguita. */
export function costoPrevistoConta(v) {
  return statoVariante(v).id !== "esclusa" && (eAccettata(v) || eEseguita(v));
}

/**
 * Stato della variante. Le voci create prima di ottobre 2026 (senza storico) erano prove:
 * "Respinta", "Da valutare" e "Approvata" senza storico → esclusa (non contano nei totali;
 * se si modificano rientrano come eseguite). "Fatturata" resta fatturata.
 */
export function statoVariante(v) {
  const s = String(v?.stato || "").toLowerCase();
  if (s === "respinta") return STATI_VARIANTE.esclusa;
  if ((s === "da valutare" || s === "approvata") && !Array.isArray(v?.storico)) return STATI_VARIANTE.esclusa;
  if (s === "fatturata" || v?.fatturata === true) return STATI_VARIANTE.fatturata;
  if (v?.fatturabile === false) return STATI_VARIANTE.nonFatturabile;
  if (importoVariante(v) === null) return STATI_VARIANTE.daValorizzare;
  const acc = eAccettata(v), ese = eEseguita(v);
  if (!acc) return ese ? STATI_VARIANTE.inAttesa : STATI_VARIANTE.offerta;
  return ese ? STATI_VARIANTE.valorizzata : STATI_VARIANTE.accettata;
}

/**
 * Riepilogo delle varianti di una commessa: è quello che si salva sul budget
 * (variantiCalcolate) perché tutte le viste usino gli stessi numeri.
 *  totale       = varianti ACCETTATE con importo (da eseguire + da fatturare + fatturate) → ricavi
 *  daFatturare  = accettate ed eseguite non ancora fatturate
 *  fatturato    = già fatturate
 *  accettateDaEseguire = accettate ma lavoro non ancora fatto (già nel totale)
 *  inAttesa     = valorizzate ma NON accettate (offerte + eseguite da accettare): fuori dai ricavi
 *  costoPrevisto = costo materiali previsto delle varianti accettate o eseguite
 *  nDaValorizzare = fatturabili senza importo (avviso)
 */
export function riepilogoVarianti(varianti = []) {
  const r = { n: 0, totale: 0, daFatturare: 0, fatturato: 0, nDaValorizzare: 0, nDaFatturare: 0, nFatturate: 0, nNonFatturabili: 0,
    accettateDaEseguire: 0, nAccettateDaEseguire: 0, inAttesa: 0, nOfferte: 0, nEseguiteDaAccettare: 0, costoPrevisto: 0 };
  varianti.forEach(v => {
    const st = statoVariante(v);
    if (st.id === "esclusa") return;
    r.n++;
    const imp = importoVariante(v) || 0;
    if (st.id === "daValorizzare") r.nDaValorizzare++;
    if (st.id === "nonFatturabile") r.nNonFatturabili++;
    if (st.id === "valorizzata") { r.totale += imp; r.daFatturare += imp; r.nDaFatturare++; }
    if (st.id === "fatturata") { r.totale += imp; r.fatturato += imp; r.nFatturate++; }
    if (st.id === "accettata") { r.totale += imp; r.accettateDaEseguire += imp; r.nAccettateDaEseguire++; }
    if (st.id === "offerta") { r.inAttesa += imp; r.nOfferte++; }
    if (st.id === "inAttesa") { r.inAttesa += imp; r.nEseguiteDaAccettare++; }
    if (costoPrevistoConta(v)) r.costoPrevisto += costoPrevistoVariante(v) || 0;
  });
  ["totale", "daFatturare", "fatturato", "accettateDaEseguire", "inAttesa", "costoPrevisto"].forEach(k => { r[k] = Math.round(r[k] * 100) / 100; });
  return r;
}

/** I due riepiloghi coincidono? (per non riscrivere il budget a ogni apertura) */
export function stessoRiepilogo(a, b) {
  if (!a || !b) return false;
  return ["n", "totale", "daFatturare", "fatturato", "nDaValorizzare", "nDaFatturare", "nFatturate", "nNonFatturabili",
    "accettateDaEseguire", "nAccettateDaEseguire", "inAttesa", "nOfferte", "nEseguiteDaAccettare", "costoPrevisto"].every(k => Math.abs((a[k] || 0) - (b[k] || 0)) < 0.005);
}

/**
 * Voci dello storico per una modifica: una riga per ogni campo cambiato.
 * L'importo e la fatturazione sono i più importanti da tracciare.
 */
const CAMPI_TRACCIATI = { titolo: "Titolo", descrizione: "Descrizione", origine: "Origine", eseguita: "Lavoro eseguito", data: "Data di esecuzione",
  accettata: "Accettata dal cliente", dataAccettazione: "Data accettazione", autorizzazione: "Riferimento accettazione",
  valoreEconomico: "Importo", valorizzataDa: "Importo deciso da", costoMaterialiPrevisto: "Costo materiali previsto", fatturabile: "Fatturabile", stato: "Stato", fatturaRif: "Riferimento fattura", dataFattura: "Data fattura" };
export function vociStorico(prima = {}, dopo = {}, chi = "", il = "") {
  const fmt = (k, v) => {
    if (k === "fatturabile") return v === false ? "no" : "sì"; // non indicato = fatturabile (valore di partenza)
    if (k === "eseguita") return v === true ? "sì" : v === false ? "no" : "—"; // non indicato: non si dà per eseguita (W409)
    if (k === "accettata") return v === true ? "sì" : "no";
    if (k === "valorizzataDa") return testoValorizzataDa({ valorizzataDa: v }) || "—";
    if (v === null || v === undefined || v === "") return "—";
    if (k === "valoreEconomico" || k === "costoMaterialiPrevisto") return "€ " + Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (k === "fatturabile") return v ? "sì" : "no";
    if (k === "origine") return ORIGINI_VARIANTE[v]?.label || v;
    return String(v);
  };
  return Object.keys(CAMPI_TRACCIATI).filter(k => k in dopo && fmt(k, prima[k]) !== fmt(k, dopo[k]))
    .map(k => ({ il, chi, campo: CAMPI_TRACCIATI[k], da: fmt(k, prima[k]), a: fmt(k, dopo[k]) }));
}

/** Righe del riepilogo delle varianti da mandare al cliente (eseguite e offerte). */
export function righeRiepilogoCliente(varianti = []) {
  return varianti.filter(v => statoVariante(v).id !== "esclusa")
    .sort((a, b) => String(a.data || "").localeCompare(String(b.data || "")))
    .map(v => ({ data: v.data || "", titolo: v.titolo || "", descrizione: v.descrizione || "", importo: importoVariante(v), stato: statoVariante(v), eseguita: eEseguita(v), accettata: eAccettata(v) }));
}

/**
 * Dettaglio per voce, per relazioni e riepiloghi (come lo scrive Paolo):
 *   Importo contrattuale € …
 *   Varianti richieste o indispensabili: n. 06 varianti come da dettaglio, per un valore
 *   calcolato pari a € … — poi le valorizzate (importo, chi l'ha deciso, autorizzata o no)
 *   e quelle ancora da valorizzare (richiesta dal cliente / variante tecnica indispensabile).
 */
export function dettaglioVarianti(varianti = []) {
  const valide = varianti.filter(v => statoVariante(v).id !== "esclusa")
    .sort((a, b) => String(a.data || a.dataOfferta || "").localeCompare(String(b.data || b.dataOfferta || "")));
  const voce = v => ({
    titolo: v.titolo || "Variante", descrizione: v.descrizione || "",
    origine: (ORIGINI_VARIANTE[v.origine] || ORIGINI_VARIANTE.richiesta_cliente).nelDocumento,
    importo: importoVariante(v), valorizzataDa: testoValorizzataDa(v),
    autorizzata: eAccettata(v), riferimento: v.autorizzazione || "", aCaricoImpresa: v.fatturabile === false,
  });
  const valorizzate = valide.filter(v => importoVariante(v) !== null).map(voce);
  const daValorizzare = valide.filter(v => importoVariante(v) === null).map(voce);
  const somma = l => Math.round(l.reduce((t, x) => t + (x.importo || 0), 0) * 100) / 100;
  const fatturabili = valorizzate.filter(x => !x.aCaricoImpresa);
  return {
    n: valide.length, valorizzate, daValorizzare,
    valoreCalcolato: somma(fatturabili),
    valoreAutorizzato: somma(fatturabili.filter(x => x.autorizzata)),
  };
}

export const nVarianti = n => String(n).padStart(2, "0");
